/**
 * Employment Form Validation Rules and Messages
 * Centralized validation logic and error messages
 */

import { EmploymentFormData, EmploymentRecord } from 'utils/employment/types';

export const VALIDATION_MESSAGES = {
  REQUIRED_FIELDS: 'FN_PLEASE_FILL_IN_ALL_REQUIRED_FIELDS_ORGANIZATION_JOB_TITLE_AND_EMPLOYMENT_TYPE',
  START_DATE_REQUIRED: 'FN_PLEASE_PROVIDE_A_START_DATE',
  END_DATE_REQUIRED: 'FN_PLEASE_PROVIDE_AN_END_DATE_OR_MARK_AS_CURRENT_POSITION',
  INVALID_DATE_FORMAT: 'FN_INVALID_DATE_FORMAT',
  START_AFTER_END: 'FN_START_DATE_CANNOT_BE_AFTER_END_DATE',
  VALIDATION_FAILED: 'FN_EMPLOYMENT_RECORD_VALIDATION_FAILED',
  FILL_CORRECTLY: 'FN_PLEASE_FILL_IN_ALL_REQUIRED_FIELDS_CORRECTLY',
  ORGANIZATION_REQUIRED: 'FN_ORGANIZATION_REQUIRED',
  JOB_TITLE_REQUIRED: 'FN_JOB_TITLE_REQUIRED',
  EMPLOYMENT_TYPE_REQUIRED: 'FN_EMPLOYMENT_TYPE_REQUIRED',
  INVALID_DISPLAY_STATUS: 'FN_DISPLAY_STATUS_MUST_BE_HIDDEN_OR_VISIBLE',
  // Work email validation messages
  WORK_EMAIL_REQUIRED: 'FN_WORK_EMAIL_REQUIRED_FOR_CURRENT_EMPLOYMENT',
  INVALID_EMAIL_FORMAT: 'FN_PLEASE_ENTER_A_VALID_EMAIL_ADDRESS',
  WORK_EMAIL_DOMAIN_MISMATCH: 'FN_WORK_EMAIL_DOMAIN_SHOULD_MATCH_THE_ORGANIZATION',
} as const;

export const VALIDATION_RULES = {
  REQUIRED_FIELDS: ['organization', 'designation'] as const,
  
  /**
   * Validates if a field is present and not null/undefined
   */
  isFieldPresent: (value: any): boolean => {
    return value !== null && value !== undefined && value !== '';
  },

  /**
   * Validates organization field - returns key name for translation
   */
  validateOrganization: (organization: any): string | null => {
    if (!organization?.id || !organization?.name) {
      return VALIDATION_MESSAGES.ORGANIZATION_REQUIRED;
    }
    return null;
  },

  /**
   * Validates designation field - returns key name for translation
   */
  validateDesignation: (designation: any): string | null => {
    if (!designation?.id || !designation?.name) {
      return VALIDATION_MESSAGES.JOB_TITLE_REQUIRED;
    }
    return null;
  },


  /**
   * Validates date format and logic - returns key name for translation
   */
  validateDateLogic: (startDate: string | null, endDate: string | null): string | null => {
    // Only validate if both dates are provided and valid
    if (startDate && endDate && endDate !== 'Present') {
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      
      if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
        return VALIDATION_MESSAGES.INVALID_DATE_FORMAT;
      } 
      
      if (startDateObj > endDateObj) {
        return VALIDATION_MESSAGES.START_AFTER_END;
      }
    }
    return null;
  },

  /**
   * Validates display status - returns key name for translation  
   */
  validateDisplayStatus: (displayStatus: number): string | null => {
    if (displayStatus !== 1 && displayStatus !== 2) {
      return VALIDATION_MESSAGES.INVALID_DISPLAY_STATUS;
    }
    return null;
  },

  /**
   * Validates work email field - returns key name for translation
   */
  validateWorkEmail: (workEmail: string | undefined, isCurrentEmployment: boolean): string | null => {
    // Work email is now optional - only validate format if provided
    if (workEmail && workEmail.trim() !== '') {
      // Basic email format validation (only if email is provided)
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(workEmail)) {
        return VALIDATION_MESSAGES.INVALID_EMAIL_FORMAT;
      }
    }
    return null;
  },
} as const;

export const validateEmploymentFormData = (formData: Partial<EmploymentFormData>): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  const orgError = VALIDATION_RULES.validateOrganization(formData.organization);
  if (orgError) errors.push(orgError);

  const designationError = VALIDATION_RULES.validateDesignation(formData.designation);
  if (designationError) errors.push(designationError);

  const dateLogicError = VALIDATION_RULES.validateDateLogic(
    formData.startDate, 
    formData.endDate
  );
  if (dateLogicError) errors.push(dateLogicError);

  if (formData.displayStatus !== undefined) {
    const displayStatusError = VALIDATION_RULES.validateDisplayStatus(formData.displayStatus);
    if (displayStatusError) errors.push(displayStatusError);
  }

  const workEmailError = VALIDATION_RULES.validateWorkEmail(
    formData.workEmail, 
    formData.isCurrentEmployment || false
  );
  if (workEmailError) errors.push(workEmailError);

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Comprehensive employment record validation function
 * (For backend data validation)
 */
export const validateEmploymentRecord = (record: Partial<EmploymentRecord>): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  // Required fields validation
  if (!record.organization?.id || !record.organization?.name) {
    errors.push(VALIDATION_MESSAGES.ORGANIZATION_REQUIRED);
  }

  if (!record.designation?.id || !record.designation?.name) {
    errors.push(VALIDATION_MESSAGES.JOB_TITLE_REQUIRED);
  }

  // Date validation
  if (record.startDate && record.endDate && record.endDate !== 'Present') {
    const startDate = new Date(record.startDate);
    const endDate = new Date(record.endDate);
    
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      errors.push(VALIDATION_MESSAGES.INVALID_DATE_FORMAT);
    } else if (startDate > endDate) {
      errors.push(VALIDATION_MESSAGES.START_AFTER_END);
    }
  }

  // Display status validation
  if (record.displayStatus !== 1 && record.displayStatus !== 2) {
    errors.push(VALIDATION_MESSAGES.INVALID_DISPLAY_STATUS);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}; 